import { useQuery } from '@tanstack/react-query';
import { PostCommentDto } from 'src/api/FiretrolApiClient';
import { postApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';

const POLL_INTERVAL = 8000;

export interface IPostCommentsOptions {
  /** The post is on screen, so its comments are kept live. */
  enabled?: boolean;
  initialData?: PostCommentDto[];
}

/** @registry Comments on a post, polled every 8s while the post is visible. */
export function usePostComments(
  jobId: number | null | undefined,
  postId: string,
  { enabled = false, initialData }: IPostCommentsOptions = {},
) {
  // A post reached from the global feed can carry no job id, and the comments
  // route is job-scoped, so it must not be fetched or polled without one.
  const live = enabled && jobId != null && jobId > 0;

  // The comment thread is keyed by post alone: the same key is what
  // `postComment.write` invalidates from any job context.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return useQuery({
    queryKey: queryKeys.posts.comments(postId),
    queryFn: ({ signal }) =>
      postApiClient.getComments(jobId!, postId, toCancelToken(signal)),
    enabled: live,
    initialData,
    refetchOnWindowFocus: true,
    refetchInterval: live ? POLL_INTERVAL : false,
    meta: { silent: true, errorContext: 'post comments' },
  });
}
